/**
 * CSRF double-submit cookie helpers for cookie-based auth.
 * Token is stored in a readable `csrf` cookie; mutating requests send X-CSRF-Token.
 */

import { devLog } from './devLog';
import { buildApiUrl } from './apiBaseUrl';

const CSRF_COOKIE_NAME = 'csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let cachedToken = null;
let bootstrapInFlight = null;

/**
 * Parse document.cookie for the CSRF token value.
 * @returns {string | null}
 */
export function readCsrfTokenFromCookie() {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }

  const prefix = `${CSRF_COOKIE_NAME}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      const value = trimmed.slice(prefix.length);
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }

  return null;
}

/**
 * @returns {string | null}
 */
export function getCsrfToken() {
  return cachedToken || readCsrfTokenFromCookie();
}

/**
 * @param {string | null | undefined} token
 */
export function setCachedCsrfToken(token) {
  cachedToken = token || null;
}

/**
 * Bootstrap CSRF token from backend (sets cookie + returns token in body).
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<string | null>}
 */
export async function bootstrapCsrfToken({ force = false } = {}) {
  if (!force) {
    const existing = getCsrfToken();
    if (existing) {
      return existing;
    }
  } else {
    clearCachedCsrfToken();
  }

  if (bootstrapInFlight) {
    return bootstrapInFlight;
  }

  bootstrapInFlight = (async () => {
    try {
      const response = await fetch(buildApiUrl('/api/auth/csrf'), {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        devLog.warn('csrf: bootstrap failed', { status: response.status });
        return null;
      }

      const result = await response.json();
      const token = result?.data?.token || readCsrfTokenFromCookie();
      if (token) {
        setCachedCsrfToken(token);
      } else {
        devLog.warn('csrf: bootstrap response missing token');
      }
      return token;
    } catch (error) {
      devLog.warn('csrf: bootstrap network error', error?.message || 'unknown');
      return null;
    } finally {
      bootstrapInFlight = null;
    }
  })();

  return bootstrapInFlight;
}

/**
 * Bootstrap CSRF token from backend when missing.
 * @returns {Promise<string | null>}
 */
export async function ensureCsrfToken() {
  return bootstrapCsrfToken({ force: false });
}

/**
 * Clear cache and fetch a fresh CSRF token from the backend.
 * @returns {Promise<string | null>}
 */
export async function rebootstrapCsrfToken() {
  return bootstrapCsrfToken({ force: true });
}

/**
 * @param {number} status
 * @param {{ code?: string }} [body]
 * @returns {boolean}
 */
export function isCsrfValidationFailedResponse(status, body) {
  return status === 403 && body?.code === 'CSRF_VALIDATION_FAILED';
}

/**
 * @param {string} [method='GET']
 * @returns {Record<string, string>}
 */
export function getCsrfHeaders(method = 'GET') {
  const normalized = String(method || 'GET').toUpperCase();
  if (!UNSAFE_METHODS.has(normalized)) {
    return {};
  }

  const token = getCsrfToken();
  if (!token) {
    return {};
  }

  return { [CSRF_HEADER_NAME]: token };
}

/**
 * After refresh/login, sync in-memory token from cookie (server may rotate).
 */
export function syncCsrfTokenFromCookie() {
  const token = readCsrfTokenFromCookie();
  if (token) {
    setCachedCsrfToken(token);
  }
  return token;
}

export function clearCachedCsrfToken() {
  cachedToken = null;
}

export { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, UNSAFE_METHODS };
